import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TextHeadingWithBody } from "@ui-kit/TextHeadingWithBody/TextHeadingWithBody";
import { ModalNavFooter } from "@ui-kit/Modal/ModalNavFooter";
import {
  setCurrentStep,
  setUserPreferenceIds,
  selectUserPreferenceIds,
  selectTripDuration,
  selectGroupComposition,
} from "@store/tripBuilder/tripBuilderReducer";
import { useAppDispatch, useAppSelector } from "@store/store";
import { TabMultiSelect } from "@ui-kit/TabButtonGroup/TabMultiSelect";
import { selectClientUserPreferences } from "@store/client/clientReducer";
import { GenerateTripPlanRequest } from "@dto/TripBuilder";
import { logError } from "@lib/logger";

interface TripBuilderUserPreferencesProps {
  generateTripPlan: (request: GenerateTripPlanRequest) => void;
}

const TripBuilderUserPreferences: FunctionComponent<
  TripBuilderUserPreferencesProps
> = ({ generateTripPlan }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userPreferenceOptions = useAppSelector(selectClientUserPreferences);
  const selectedUserPreferenceIds = useAppSelector(selectUserPreferenceIds);
  const tripDuration = useAppSelector(selectTripDuration);
  const groupComposition = useAppSelector(selectGroupComposition);

  const afterTabSelect = useCallback(
    (tabValues: number[]) => {
      dispatch(setUserPreferenceIds(tabValues));
    },
    [dispatch],
  );

  const goBack = useCallback(() => {
    dispatch(setCurrentStep("groupComposition"));
  }, [dispatch]);

  const goNext = useCallback(() => {
    if (tripDuration && groupComposition && selectedUserPreferenceIds) {
      generateTripPlan({
        tripDuration,
        groupComposition,
        userPreferenceIds: selectedUserPreferenceIds,
      });
      dispatch(setCurrentStep("loading"));
    } else {
      logError("TripBuilder: Missing required parameters");
    }
  }, [
    dispatch,
    generateTripPlan,
    groupComposition,
    selectedUserPreferenceIds,
    tripDuration,
  ]);

  return (
    <>
      <div className="trip-builder-container">
        <TextHeadingWithBody
          headingText={t("screens.tripBuilder.userPreferences.heading")}
          bodyText={t("screens.tripBuilder.userPreferences.subheading")}
          headingSize="Xlarge"
          bodySize="Large"
          align="center"
          gap={8}
        />
        <div className="preferences-container" style={{ marginTop: "24px" }}>
          <TabMultiSelect
            tabs={userPreferenceOptions.map((preference) => ({
              value: preference.id,
              label: preference.name,
            }))}
            afterTabSelect={afterTabSelect}
            testID="user-preferences"
            variant="outline"
          />
        </div>
      </div>
      <ModalNavFooter
        onBack={goBack}
        onNext={goNext}
        nextText={t("screens.tripBuilder.createPlan")}
        nextDisabled={selectedUserPreferenceIds.length === 0}
      />
    </>
  );
};

export { TripBuilderUserPreferences };
